import { ExternalLink } from "@octopusdeploy/design-system-components";
import type { LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useState } from "react";
import isWithinLicenceLimit from "~/areas/configuration/components/License/isWithinLicenceLimit";
import kubernetesCategoryDefinition from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesCategoryDefinition";
import type { CategorizedEndpointRegistration, CategoryDefinition, EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import endpointRegistry, { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { repository } from "~/clientInstance";
import DataLoader from "~/components/DataLoader";
import Dialog from "~/components/Dialog/Dialog";
import { WorkerLimitReachedDialogLayout } from "~/components/LicenseLimitReachedDialogLayout";
import { PageContent } from "~/components/PageContent/PageContent";
import { PageLoading } from "~/components/PageContent/PageLoading";
import { useSpaceAwareNavigation } from "../../../../components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { EndpointSelector } from "../MachineSettings/Endpoints/EndpointSelector";
const WorkerMachineNewDataLoader = DataLoader<LoadedData>();
interface LoadedData {
    licenseStatus: LicenseStatusResource;
    registrations: EndpointRegistration[];
    defaultCategory: string;
}
export interface WorkerMachineNewProps {
    spaceId: string;
    workerPoolId: string | undefined;
}
export function SelectWorkerTypePage(props: WorkerMachineNewProps) {
    return (<WorkerMachineNewDataLoader load={async () => {
            const licenseStatus = repository.Licenses.getCurrentStatus();
            const registrations = endpointRegistry.getAllMachines();
            return {
                registrations,
                licenseStatus: await licenseStatus,
                defaultCategory: kubernetesCategoryDefinition.category,
            };
        }} renderAlternate={({ errors }) => <PageLoading errors={errors} loadingTitle="Add Worker"/>} renderWhenLoaded={(loadedData) => <WorkerMachineNewWhenLoaded spaceId={props.spaceId} loadedData={loadedData} workerPoolId={props.workerPoolId}/>}/>);
}
interface WorkerMachineNewWhenLoadedProps {
    spaceId: string;
    loadedData: LoadedData;
    workerPoolId: string | undefined;
}
function WorkerMachineNewWhenLoaded({ loadedData, workerPoolId, spaceId }: WorkerMachineNewWhenLoadedProps) {
    const { licenseStatus, registrations, defaultCategory } = loadedData;
    const navigate = useNavigateToWorker(spaceId, workerPoolId);
    const isWithinWorkerLimit = isWithinLicenceLimit(licenseStatus, "Workers");
    const [isUpgradeDialogOpen, setUpgradeDialogOpen] = useState(!isWithinWorkerLimit);
    const categories = endpointRegistry.categorizeEndpoints(registrations);
    //we default to the linux category definition as default for the workers
    return (<PageContent header={{ title: "Add Worker", breadcrumbs: [{ label: "Workers", pageUrl: links.workersPage.generateUrl({ spaceId }) }] }}>
            <EndpointSelector spaceId={spaceId} heading={<Heading />} registrations={registrations} categories={categories} defaultCategory={defaultCategory} onSelect={navigate} scope={EndpointSelectionScope.Worker} showUpsellExperience={false}/>

            <Dialog open={isUpgradeDialogOpen}>
                <WorkerLimitReachedDialogLayout onActionClick={() => {
            setUpgradeDialogOpen(false);
            window.history.back();
        }}/>
            </Dialog>
        </PageContent>);
}
function Heading() {
    return (<React.Fragment>
            What type of <strong>Worker</strong> do you want to set up? Learn more about <ExternalLink href="Worker">Workers</ExternalLink>
        </React.Fragment>);
}
function useNavigateToWorker(spaceId: string, workerPoolId: string | undefined) {
    const navigation = useSpaceAwareNavigation();
    return React.useCallback((registration: CategorizedEndpointRegistration, category: CategoryDefinition) => {
        if (endpointRegistry.isMachineRegistration(registration) && registration.discoverable) {
            return navigation.navigate(links.discoverWorkerPage.generateUrl({ spaceId, endpointKey: registration.key }, { category: category.category, workerPoolId }));
        }
        else {
            return navigation.navigate(links.addWorkerPage.generateUrl({ spaceId }, { type: registration.communicationStyle, environment: workerPoolId }));
        }
    }, [workerPoolId, navigation, spaceId]);
}
